import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactElement, cloneElement } from 'react';
import { useIntl } from 'react-intl';

import {
  UserSportStatisticFormat,
  UserSportStatisticSlug,
} from '@sorare/core/src/__generated__/globalTypes';
import { ArenaAlt } from '@sorare/core/src/atoms/icons/ArenaAlt';
import { BallAlt } from '@sorare/core/src/atoms/icons/BallAlt';
import { Card } from '@sorare/core/src/atoms/icons/Card';
import { MyCards } from '@sorare/core/src/atoms/icons/MyCards';
import { StarEmpty } from '@sorare/core/src/atoms/icons/StarEmpty';
import { StatDisplay } from '@sorare/core/src/atoms/ui/StatDisplay';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';

import { ManagerIDStat_userSportStatistic } from './__generated__/index.graphql';

type Props = {
  statistic: ManagerIDStat_userSportStatistic | null;
};

const cardsOwnedIcon = (
  <MyCards
    size={4}
    color={
      <linearGradient
        x1="15.6676"
        y1="24.6476"
        x2="8.67626"
        y2="19.2614"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#F1FF00" />
        <stop offset="1" stopColor="#3DDC84" />
      </linearGradient>
    }
  />
);
const ICONS_MAP: {
  [key in UserSportStatisticSlug]?: ReactElement;
} = {
  [UserSportStatisticSlug.PRO_CARDS_OWNED]: cardsOwnedIcon,
  [UserSportStatisticSlug.CARDS_OWNED]: cardsOwnedIcon,
  [UserSportStatisticSlug.PRO_CARDS_WON_COUNT]: (
    <Card size={4} gradient="blue" />
  ),
  [UserSportStatisticSlug.CARDS_WON_COUNT]: <Card size={4} gradient="blue" />,
  [UserSportStatisticSlug.RIVALS_ARENA_WIN]: (
    <ArenaAlt
      size={4}
      color={
        <linearGradient
          x1="32.4437"
          y1="32.7086"
          x2="23.112"
          y2="14.2917"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#3DDC84" />
          <stop offset="1" stopColor="#00FFF7" />
        </linearGradient>
      }
    />
  ),
  [UserSportStatisticSlug.TOTAL_COLLECTION_SCORE]: (
    <StarEmpty
      size={4}
      color={
        <linearGradient
          x1="30.3143"
          y1="35.7739"
          x2="17.0124"
          y2="17.0501"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FF0974" />
          <stop offset="1" stopColor="#AD00FF" />
        </linearGradient>
      }
    />
  ),
  [UserSportStatisticSlug.RIVALS_GAME_PLAYED_COUNT]: (
    <BallAlt
      size={4}
      color={
        <linearGradient
          x1="23.5219"
          y1="29.8962"
          x2="13.8798"
          y2="4.05205"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FF6BAC" />
          <stop offset="1" stopColor="#FF0974" />
        </linearGradient>
      }
    />
  ),
};

export const ManagerIDStat = ({ statistic }: Props) => {
  const { formatDate, formatNumber } = useIntl();
  const isTabletAndAbove = useIsTabletAndAbove();
  if (!statistic) {
    return null;
  }
  const icon = ICONS_MAP[statistic.name];
  return (
    <>
      {statistic.format === UserSportStatisticFormat.NUMBER && (
        <StatDisplay
          icon={
            icon &&
            cloneElement(icon, {
              size: isTabletAndAbove ? 4 : 2.5,
            })
          }
          label={statistic.title}
        >
          {formatNumber(statistic.value, {
            compactDisplay: 'short',
            notation: 'compact',
            maximumFractionDigits: 1,
          })}
        </StatDisplay>
      )}
      {statistic.format === UserSportStatisticFormat.TIMESTAMP && (
        <StatDisplay label={statistic.title}>
          {formatDate(statistic.value * 1000, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })}
        </StatDisplay>
      )}
    </>
  );
};

ManagerIDStat.fragments = {
  userSportStatistic: gql`
    fragment ManagerIDStat_userSportStatistic on UserSportStatistic {
      id
      name
      value
      format
      title
    }
  ` as TypedDocumentNode<ManagerIDStat_userSportStatistic>,
};
